import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS as ContentfulBlocks,
  INLINES as ContentfulInlines,
  type Document as ContentfulDocument,
  BLOCKS,
} from '@contentful/rich-text-types';
import { logger } from '@tuic/logger';
import { MeinSchiffText } from '@tuic/web-compositions/shared/MeinSchiffText';

import type {
  NodeRenderer as ContentfulNodeRenderer,
  Options as ContentfulRenderOptions,
} from '@contentful/rich-text-react-renderer';
import type {
  Block as ContentfulBlock,
  Inline as ContentfulInline,
} from '@contentful/rich-text-types';
import type { ReactNode } from 'react';

export type WrappingClosure = (tree: ReactNode) => ReactNode;

export type DocumentToReactTreeArg = ContentfulRenderOptions & {
  document?: ContentfulDocument;
  wrap?: WrappingClosure;
};

export type {
  ContentfulNodeRenderer,
  ContentfulDocument,
  ContentfulBlock,
  ContentfulInline,
};
export { ContentfulBlocks, ContentfulInlines };

/**
 * Converts given contentful document to a React component tree.
 * You can provide a closure if you want to customize the wrapper of the returned tree.
 * The closure is only invoked when the document renders successfully to a React tree.
 * Additionally, you can provide custom renderers for nodes, texts and marks.
 *
 * ```tsx
 * const contentfulDocument = fetchDocument();
 *
 * const tree = documentToReactTree(contentfulDocument);
 *
 * const wrappedTree = documentToReactTree(contentfulDocument, tree => (
 *  <div className="[&_a]:underline [&_a:hover]:no-underline">
 *    {tree}
 *  </div>
 * ));
 *
 * const treeWithRenderer = documentToReactTree(contentfulDocument, undefined, {
 *   renderNode: {
 *     [BLOCKS.PARAGRAPH]: (_, children) => <div className="text-sm">{children}</div>,
 *   },
 * );
 * ```
 */
export function documentToReactTree(
  docOrDocWithOptions: ContentfulDocument | Record<string, any> | undefined,
  wrap?: WrappingClosure,
  customOptions?: ContentfulRenderOptions,
): ReactNode | null;

/**
 * Converts given contentful document to a React component tree.
 * You can provide a closure if you want to customize the wrapper of the returned tree.
 * The closure is only invoked when the document renders successfully to a React tree.
 * Additionally, you can provide custom renderers for nodes, texts and marks.
 *
 * ```tsx
 * const contentfulDocument = fetchDocument();
 *
 * const tree = documentToReactTree({
 *   document: contentfulDocument,
 *   wrap: tree => (
 *    <div className="[&_a]:underline [&_a:hover]:no-underline">
 *      {tree}
 *    </div>
 *   ),
 *   renderNode: {
 *     [BLOCKS.PARAGRAPH]: (_, children) => <div className="text-sm">{children}</div>,
 *   }
 * });
 *
 * const wrappedTree = documentToReactTree(contentfulDocument, tree => (
 *  <div className="[&_a]:underline [&_a:hover]:no-underline">
 *    {tree}
 *  </div>
 * ));
 *
 * const treeWithRenderer = documentToReactTree(contentfulDocument, undefined, {
 *   renderNode: {
 *     [BLOCKS.PARAGRAPH]: (_, children) => <div className="text-sm">{children}</div>,
 *   },
 * );
 * ```
 */
export function documentToReactTree(
  arg: DocumentToReactTreeArg | undefined,
): ReactNode | null;

export function documentToReactTree(
  docOrGroupedArg:
    | ContentfulDocument
    | Record<string, any>
    | DocumentToReactTreeArg
    | undefined,
  wrapArg?: WrappingClosure,
  optionsArg?: ContentfulRenderOptions,
): ReactNode | null {
  const { document, wrap, ...cfRenderOptions } =
    parseDocumentToReactTreeSignature(docOrGroupedArg, wrapArg, optionsArg);

  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const tree = documentToReactComponents(document, {
      renderText,
      ...cfRenderOptions,
    });

    if (!wrap) {
      return tree;
    }

    return wrap(tree);
  } catch (e) {
    logger.error('Could not parse text document to component tree.');
    logger.error(JSON.stringify(document));
    logger.error(e);
  }

  return null;
}

const isDocumentToReactTreeArg = (
  docOrDocWithOptions: ContentfulDocument | DocumentToReactTreeArg | undefined,
): docOrDocWithOptions is DocumentToReactTreeArg => {
  return (
    typeof docOrDocWithOptions === 'object' &&
    docOrDocWithOptions !== null &&
    'document' in docOrDocWithOptions
  );
};

const parseDocumentToReactTreeSignature = (
  docOrGroupedArg:
    | ContentfulDocument
    | Record<string, any>
    | DocumentToReactTreeArg
    | undefined,
  wrapArg?: WrappingClosure,
  optionsArg?: ContentfulRenderOptions,
): DocumentToReactTreeArg => {
  if (typeof docOrGroupedArg === 'undefined') {
    return {};
  }

  if (isDocumentToReactTreeArg(docOrGroupedArg)) {
    return docOrGroupedArg;
  }

  return {
    ...optionsArg,
    document: docOrGroupedArg as ContentfulDocument,
    wrap: wrapArg,
  };
};

/**
 * By default, Contentful will replace line breaks added to the Rich Text editor with /n Here we translate it back to html-line-breaks.
 */
const renderText = (text: string): ReactNode[] => {
  return text.split('\n').reduce<ReactNode[]>((elementList, text, index) => {
    if (index > 0) {
      elementList.push(<br key={`b${index}`} />);
    }

    if (text.includes('Mein Schiff')) {
      elementList.push(
        <MeinSchiffText key={`ms${index}`}>{text}</MeinSchiffText>,
      );

      return elementList;
    }

    elementList.push(text);

    return elementList;
  }, []);
};

export const documentToReactTreeInline = (
  doc: Document | undefined | Record<string, any>,
): ReactNode => {
  return documentToReactTree(doc, undefined, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => children,
    },
  });
};
